import { useEffect } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { theme } from '../../theme';

type Variant = 'danger' | 'warning' | 'success' | 'neutral';

interface AnimatedMetricProps {
  label: string;
  value: number | string;
  prefix?: string;
  suffix?: string;
  variant?: Variant;
}

function AnimatedNumber({ value, prefix = '', suffix = '' }: { value: number; prefix?: string; suffix?: string }) {
  const spring = useSpring(0, { stiffness: 50, damping: 18 });
  const display = useTransform(spring, (v) =>
    `${prefix}${v % 1 !== 0 ? v.toFixed(2) : Math.round(v).toLocaleString()}${suffix}`
  );
  useEffect(() => { spring.set(value); }, [value, spring]);
  return <motion.span>{display}</motion.span>;
}

export const AnimatedMetric = ({
  label, value, prefix = '', suffix = '', variant = 'neutral',
}: AnimatedMetricProps) => {
  const color = theme.variantColors[variant];

  return (
    <div style={{
      background: theme.semantic.bg.surface,
      borderLeft: `${theme.borders.heavy} ${color}`,
      padding: `${theme.spacing[4]} ${theme.spacing[5]}`,
    }}>
      {/* Label */}
      <div style={{
        fontFamily: theme.typography.fontFamily.display,
        fontSize: theme.typography.fontSize.xs,
        fontWeight: theme.typography.fontWeight.bold,
        color: theme.semantic.text.muted,
        letterSpacing: theme.typography.letterSpacing.widest,
        textTransform: 'uppercase' as const,
        marginBottom: theme.spacing[2],
      }}>
        {label}
      </div>

      {/* Value */}
      <div style={{
        fontFamily: theme.typography.fontFamily.mono,
        fontSize: theme.typography.fontSize['3xl'],
        fontWeight: theme.typography.fontWeight.bold,
        color: theme.semantic.text.primary,
        lineHeight: theme.typography.lineHeight.tight,
      }}>
        {typeof value === 'number' ? (
          <AnimatedNumber value={value} prefix={prefix} suffix={suffix} />
        ) : (
          <span>{prefix}{value}{suffix}</span>
        )}
      </div>
    </div>
  );
};

export default AnimatedMetric;
